import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /**
   * Close the mobile navigation drawer when opening the cart so two overlays do not stack
   * (e.g. quick add on collection while the hamburger menu is open).
   */
  #closeHeaderDrawerIfOpen() {
    document.querySelectorAll('header-drawer').forEach((el) => {
      const headerDrawer = /** @type {HTMLElement & { isOpen?: boolean; close?: () => void }} */ (el);
      if (headerDrawer.isOpen && typeof headerDrawer.close === 'function') {
        headerDrawer.close();
      }
    });
  }

  /**
   * @override
   */
  showDialog() {
    this.#closeHeaderDrawerIfOpen();
    super.showDialog();
  }
  
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener('dialog:close', this.#handleDrawerClosed);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener('dialog:close', this.#handleDrawerClosed);
  }

  #handleDrawerClosed = () => {
    document.dispatchEvent(new CustomEvent('cart:drawer:closed', {
      bubbles: true,
      detail: {
        source: 'cart-drawer-component',
        data: {
          source: 'cart-drawer-component'
        }
      }
    }));
  };

  /** @param {Event & { detail?: { data?: { source?: string } } }} event */
  #handleCartAdd = (event) => {
    const source = event?.detail?.data?.source;
    // Ignore cart updates that are NOT genuine add-to-cart actions.
    // CartAddEvent and CartUpdateEvent share the same event name ('cart:update'),
    // so we must filter out quantity changes, removals, and progress-bar gift toggles.
    if (
      source === 'cart-items-component' ||
      source === 'cart-progress-bar' ||
      source === 'bv-cart-progress' ||
      source === 'pageshow'
    ) return;

    if (this.hasAttribute('auto-open')) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Already open: skip — callers refresh CART context via dialog.open check
          if (this.refs.dialog?.open) return;
          this.open();
        });
      });
    }
  };

  // @ts-ignore
  #hasContent(el) {
    return el && el.textContent.trim().length > 0;
  }

  // @ts-ignore
  #extractCount(text) {
    const match = text.replace(/\s+/g, ' ').match(/\d+/);
    return match ? parseInt(match[0], 10) : 0;
  }

  // @ts-ignore
  #handleDefaultActiveTab() {
    const SEL_NORMAL  = '.normal-cart-count';
    const SEL_AUCTION = '.ra-auction-cart-count > .bv-mini-cart__tab-label';
    // @ts-ignore
    const normalEl  = document.querySelector(SEL_NORMAL);
    // @ts-ignore
    const auctionEl = document.querySelector(SEL_AUCTION);
    // @ts-ignore
    const normalCount  = this.#extractCount(normalEl.textContent);
    // @ts-ignore
    const auctionMatch = auctionEl.textContent.match(/\((\d+)\)/);
    // @ts-ignore
    const auctionCount = auctionMatch ? parseInt(auctionMatch[1], 10) : 0;
    if (!normalCount && auctionCount > 0) {
      // @ts-ignore
      document.querySelector('#tab-auctions').click();
    };
  }

  #dispatchDrawerOpened() {
    document.dispatchEvent(new CustomEvent('cart:drawer:opened', {
      bubbles: true,
      detail: {
        source: 'cart-drawer-component',
        data: {
          source: 'cart-drawer-component'
        }
      }
    }));
  }

  open() {
    // Handle here if only available RA cart then default select it
    // @ts-ignore
    this.#handleDefaultActiveTab();

    const dialog = this.refs.dialog;
    // showDialog() opens via rAF — wait until dialog:open so consumers see dialog.open === true
    if (dialog?.open) {
      this.#dispatchDrawerOpened();
    } else {
      this.addEventListener('dialog:open', () => this.#dispatchDrawerOpened(), { once: true });
      this.showDialog();
    }

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
